import { useRef } from "react";

// Type
import { LD5Chart } from "store/slice/charts/chartsSlice.types";
import { TMoreOptions } from "components/Projection/ProjectionBody/chartsListConfig";
import theme from "assets/styles/themes";
import { createHighchartsSvg, TValidSVGElements } from "utils";

interface SVGConfigElement {
    config: any;
    svg: Highcharts.SVGElement;
}
interface ArchetypeLocation {
    g: SVGConfigElement;
    circle: SVGConfigElement;
    label: SVGConfigElement;
}
interface AxesTitles {
    label: SVGConfigElement;
}

export const RADIUS_PROPORTION = 0.12;

const useBalanceWatermark = (chartCircles: boolean) => {
    const archetypeLocations = useRef<ArchetypeLocation[]>([]);
    const axesTitles = useRef<AxesTitles[]>([]);

    const renderArchetypeLocations = (
        chart: Highcharts.Chart,
        chartData: LD5Chart,
        moreOptions?: TMoreOptions,
        isExporting?: boolean
    ) => {
        const renderer = chart.renderer as Highcharts.SVGRenderer;
        const radius = Math.min(chart.plotWidth, chart.chartHeight) * RADIUS_PROPORTION;

        if (isExporting) {
            archetypeLocations.current.forEach((archetype) => {
                const group = {
                    config: archetype.g.config,
                    svg: renderer
                        .g(archetype.g.config.args)
                        .translate(...(archetype.g.config.translate as [number, number]))
                        .css(archetype.g.config.css)
                        .add(),
                };
                Object.entries(archetype).forEach((el) => {
                    if (el[0] === "g") {
                        createHighchartsSvg(chart.renderer, el[1].config, el[0] as TValidSVGElements);
                    } else {
                        createHighchartsSvg(chart.renderer, el[1].config, el[0] as TValidSVGElements, group.svg);
                    }
                });
            });
            return;
        }

        if (archetypeLocations.current.length) {
            archetypeLocations.current.forEach((el) => {
                Object.values(el).forEach((el) => el.svg.destroy());
            });
        }

        chartData.watermarks.forEach((data, i) => {
            archetypeLocations.current[i] = {
                g: {} as SVGConfigElement,
                circle: {} as SVGConfigElement,
                label: {} as SVGConfigElement,
            };

            // Group
            const groupConfig = {
                args: `archetype-${i}`,
                translate: [chart.xAxis[0].toPixels(data.x, true), chart.yAxis[0].toPixels(data.y, true)],
                css: { opacity: chartCircles ? 1 : 0 },
            };
            archetypeLocations.current[i].g = {
                config: groupConfig,
                svg: renderer
                    .g(groupConfig.args)
                    .translate(...(groupConfig.translate as [number, number]))
                    .css(groupConfig.css)
                    .add(),
            };

            // Circle
            const circleConfig = {
                args: [0, 0, radius],
                attr: {
                    fill: theme.palette.others.greyBlue,
                    "stroke-width": 0,
                    "fill-opacity": 0.15,
                },
            };
            archetypeLocations.current[i].circle = {
                config: circleConfig,
                svg: renderer
                    .circle(...circleConfig.args)
                    .attr(circleConfig.attr)
                    .add(archetypeLocations.current[i].g.svg),
            };

            // Label
            const labelConfig = {
                args: [data.label, 0, 0],
                attr: { zIndex: 4 },
                css: {
                    color: "#fff",
                    fontFamily: "Lato, sans-serif",
                    fontSize: `${radius / 4}px`,
                    fontWeight: "bolder",
                },
            };
            archetypeLocations.current[i].label = {
                config: labelConfig,
                svg: renderer
                    .label(...(labelConfig.args as [string, number, number]))
                    .attr(labelConfig.attr)
                    .css(labelConfig.css)
                    .add(archetypeLocations.current[i].g.svg),
            };

            // Adjust label position
            const { circle, label } = archetypeLocations.current[i];
            const labelX = circle.svg.getBBox().x + circle.svg.getBBox().width / 2 - label.svg.getBBox().width / 2;
            const labelY = circle.svg.getBBox().y + circle.svg.getBBox().height / 2 - label.svg.getBBox().height / 2;

            archetypeLocations.current[i].label.config = {
                ...archetypeLocations.current[i].label.config,
                args: [archetypeLocations.current[i].label.config.args[0], labelX, labelY],
            };
            archetypeLocations.current[i].label.svg.attr({
                x: labelX,
                y: labelY,
            });
        });
    };

    const toggleShowArchetypeLocations = (showArchetypes: boolean) => {
        if (archetypeLocations.current.length) {
            archetypeLocations.current.forEach((el) => {
                el.g.svg.css({ opacity: showArchetypes ? 1 : 0 });
            });
        }
    };

    const renderAxesTitles = (
        chart: Highcharts.Chart,
        chartData: LD5Chart,
        moreOptions?: TMoreOptions,
        isExporting?: boolean
    ) => {
        const renderer = chart.renderer as Highcharts.SVGRenderer;
        const titles = Object.values(chartData.titles).flat();

        if (isExporting) {
            axesTitles.current.forEach((el) => {
                renderer
                    .label(...(el.label.config.args as [string, number, number]))
                    .css(el.label.config.css as Highcharts.CSSObject)
                    .attr(el.label.config.attr)
                    // @ts-ignore
                    .add();
            });
            return;
        }

        if (axesTitles.current.length) {
            axesTitles.current.forEach((el) => {
                el.label.svg.destroy();
            });
        }

        titles.forEach((labelString, i) => {
            axesTitles.current[i] = { label: {} as SVGConfigElement };

            // Label
            const labelConfig = {
                args: [labelString, 0, 0],
                attr: {
                    fill: "#FFF",
                    zIndex: 1,
                    rotation: i > 1 ? 270 : 0,
                },
                css: {
                    color: theme.palette.others.greyBlue,
                    fontFamily: "Lato, sans-serif",
                    fontSize: "10px",
                    fontWeight: "bold",
                },
            };

            axesTitles.current[i].label = {
                config: labelConfig,
                svg: renderer
                    .label(...(labelConfig.args as [string, number, number]))
                    .attr(labelConfig.attr)
                    .css(labelConfig.css)
                    .add(),
            };

            let coors: number[] = [];
            const labelSVG = axesTitles.current[i].label.svg;

            if (i === 0) {
                coors = [
                    chart.xAxis[0].toPixels(-130, true),
                    chart.yAxis[0].toPixels(0, true) - labelSVG.getBBox().height / 2,
                ];
            }
            if (i === 1) {
                coors = [
                    chart.xAxis[0].toPixels(130, true) - labelSVG.getBBox().width,
                    chart.yAxis[0].toPixels(0, true) - labelSVG.getBBox().height / 2,
                ];
            }
            if (i === 2) {
                coors = [
                    chart.xAxis[0].toPixels(0, true) - labelSVG.getBBox().height / 1.85,
                    chart.yAxis[0].toPixels(130, true) + labelSVG.getBBox().width - 10,
                ];
            }
            if (i === 3) {
                coors = [
                    chart.xAxis[0].toPixels(0, true) - labelSVG.getBBox().height / 1.85,
                    chart.yAxis[0].toPixels(-133, true),
                ];
            }

            // Adjust label position
            axesTitles.current[i].label.config = {
                ...axesTitles.current[i].label.config,
                args: [axesTitles.current[i].label.config.args[0], coors[0], coors[1]],
            };
            axesTitles.current[i].label.svg.attr({ x: coors[0], y: coors[1] });
        });
    };

    return { renderArchetypeLocations, toggleShowArchetypeLocations, renderAxesTitles };
};

export default useBalanceWatermark;
